/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/common/types/Memory.ts
 */

// common.types.Memory.ts
import type { CalendarEvent } from './Calendar';
import type { Doc } from './Doc';
import type { Transcript, TranscriptSummary, TranscriptWithMessages } from './Transcript';

// The root memory object to which user data stems from
export type Memory = {
  id: string;
  name: string;
  time: string;
  endTime: string;
  datetime: string;
  href: string;
  transcriptId?: string;
  workspaceId: string | undefined;
  calendarEvent: Partial<CalendarEvent>;
  source: 'recording' | 'calendar';
  dateStrLocal: string;
  transcript: Transcript;
};

export type MemoryWithMessages = Memory & {
  transcript: TranscriptWithMessages;
};

export type MemoryData = {
  id?: string; // Optional for creation scenarios
  name: string;
  timestamp: string; // Renamed to match your Prisma model
  datetime: string;
  href: string;
  transcriptId?: string;
  calendarEventId?: string;
  userId: string; // Assuming there's a user relationship
  source: 'recording' | 'calendar';
  events?: MemoryDay[]; // Optional array of MemoryDay, depending on how you manage nested events
  docs?: Doc[]; // Assuming there's a related Doc type
};

// Aggregation of memories by date for use with the calendar
export type MemoryDay = {
  date: string;
  isCurrentMonth?: boolean;
  isToday?: boolean;
  isSelected?: boolean;
  events: Memory[];
};

// Local type definition to avoid Prisma dependency
export interface PrismaMemoryUpsertPayload {
  id: string;
  name: string;
  timestamp: Date;
  userId: string;
  transcriptId: string | null;
  calendarEventId: string | null;
  source: 'recording' | 'calendar';
  createdAt: Date | null;
  updatedAt: Date;
  deletedAt: Date | null;
  workspaceId: string | null;
  transcript?: Transcript | null;
  calendarEvent?: CalendarEvent | null;
}

export interface MemoryItemType {
  id: string;
  title: string;
  time: string;
  fullDate: string;
  summary: string;
  workspaceId: string | undefined;
  isDeleting?: boolean;
  isFavorite?: boolean;
}

export interface MemoryTab {
  id: 'summary' | 'transcription' | 'notes' | 'text' | 'event' | 'threads';
  label: string;
  shortLabel: string;
  icon?: string;
  description?: string;
}

export interface TranscriptInfo {
  viewerTranscript: Transcript | undefined;
  viewerTranscriptLoaded: boolean;
  assistantTranscript: Transcript | undefined;
  assistantTranscriptLoaded: boolean;
  viewerMemory: Memory | undefined;
  audioDetected: boolean | undefined;
  isRecording: boolean;
  isStopping: boolean;
  isAborting: boolean;
  isConvertingWav: boolean;
  isTranscribing: boolean;
  isFinalTranscribing: boolean;
  isCanceling: boolean;
  isSummarizing: boolean;
  isSameTranscript: boolean | undefined;
  assistantAvailable: boolean;
  canExportTranscript: boolean;
  canUnloadTranscript: boolean;
  canStartRecording: boolean;
  canStopRecording: boolean;
  canStartTranscribing: boolean;
  canStopTranscribing: boolean;
  canStartSummarizing: boolean;
  canStopSummarizing: boolean;
  canDeleteViewerTranscript: boolean;
  canEditViewerTranscript: boolean;
  canDeleteRecordings: boolean;
  hasSufficientText: boolean;
  hasSufficientUserNotes: boolean;
  hasSufficentViewerAudio: boolean;
  hasSufficientAssistantAudio: boolean;
  hasSummary: boolean;
  recordProgress: number;
  summarizeLabel: string;
  deviceName: string | undefined;
  isPreMeeting: boolean;
}

export type MemoryItem = TranscriptSummary | Transcript;

// Placeholder text for different capture types
export const CAPTURE_TYPE_PLACEHOLDERS: Record<string, string> = {
	'quick-capture': 'Quick capture',
	'meeting-notes': 'New meeting',
	'1-1-notes': 'New 1:1',
	'research-notes': 'New research',
	'qa-session': 'New Q&A',
	'interview-notes': 'New interview',
	'action-plan': 'New action plan',
	'decision-record': 'New decision',
	'idea-brainstorm': 'New idea'
};

// Default placeholder when capture type is unknown
export const DEFAULT_CAPTURE_PLACEHOLDER = 'New meeting';

// Set of all placeholder values for fast lookup
export const PLACEHOLDER_VALUES = new Set(Object.values(CAPTURE_TYPE_PLACEHOLDERS));

/**
 * Get placeholder text for a given capture type
 */
export function getCaptureTypePlaceholder(captureType: string | undefined): string {
	return CAPTURE_TYPE_PLACEHOLDERS[captureType || ''] || DEFAULT_CAPTURE_PLACEHOLDER;
}

/**
 * Check if a name is a placeholder/default value
 */
export function isPlaceholderName(name: string | undefined): boolean {
	if (!name) return true;
	const trimmed = name.trim();
	return trimmed === '' || PLACEHOLDER_VALUES.has(trimmed) || trimmed.startsWith('manual-');
}
